/**
 * Status Colors Constants
 * 
 * Centralized color definitions for different statuses across the application.
 * Use these constants to ensure consistent coloring for the same status across different sections.
 */

import { ICONS } from './icons'

export const STATUS_COLORS = {
  // Total/Summary - Blue
  TOTAL: {
    from: 'from-blue-500',
    to: 'to-blue-600',
    bg: 'bg-blue-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-blue-600',
    darkText: 'dark:text-blue-400',
    icon: ICONS.dashboard,
    // StatCard compatibility
    gradient: 'bg-blue-50 dark:bg-slate-800',
    border: 'border-blue-200 dark:border-blue-500',
    textLabel: 'text-blue-700 dark:text-blue-300',
    textValue: 'text-blue-800 dark:text-blue-200',
    textSubtext: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    iconColor: 'text-blue-500 dark:text-blue-400',
    // Badge compatibility
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  // Open/New - Red (needs attention)
  OPEN: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.warning,
    // StatCard compatibility
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    // Badge compatibility
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  // Draft/Not Started - Sky (waiting to begin)
  DRAFT: {
    from: 'from-sky-500',
    to: 'to-sky-600',
    bg: 'bg-sky-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-sky-600',
    darkText: 'dark:text-sky-400',
    icon: ICONS.clock,
    // StatCard compatibility
    gradient: 'bg-sky-50 dark:bg-slate-800',
    border: 'border-sky-200 dark:border-sky-500',
    textLabel: 'text-sky-700 dark:text-sky-300',
    textValue: 'text-sky-800 dark:text-sky-200',
    textSubtext: 'text-sky-600 dark:text-sky-400',
    iconBg: 'bg-sky-50 dark:bg-sky-900/40',
    iconColor: 'text-sky-500 dark:text-sky-400',
    // Badge compatibility
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  // In Progress - Yellow (active work)
  IN_PROGRESS: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: ICONS.lightning,
    // StatCard compatibility
    gradient: 'bg-yellow-50 dark:bg-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    // Badge compatibility
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  // In Review - Orange (needs verification)
  IN_REVIEW: {
    from: 'from-orange-500',
    to: 'to-orange-600',
    bg: 'bg-orange-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-orange-600',
    darkText: 'dark:text-orange-400',
    icon: ICONS.eye,
    // StatCard compatibility
    gradient: 'bg-orange-50 dark:bg-slate-800',
    border: 'border-orange-200 dark:border-orange-500',
    textLabel: 'text-orange-700 dark:text-orange-300',
    textValue: 'text-orange-800 dark:text-orange-200',
    textSubtext: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-50 dark:bg-orange-900/40',
    iconColor: 'text-orange-500 dark:text-orange-400',
    // Badge compatibility
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  // Testing - Purple (being tested)
  TESTING: {
    from: 'from-purple-500',
    to: 'to-purple-600',
    bg: 'bg-purple-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-purple-600',
    darkText: 'dark:text-purple-400',
    icon: ICONS.checkCircle,
    // StatCard compatibility
    gradient: 'bg-purple-50 dark:bg-slate-800',
    border: 'border-purple-200 dark:border-purple-500',
    textLabel: 'text-purple-700 dark:text-purple-300',
    textValue: 'text-purple-800 dark:text-purple-200',
    textSubtext: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/40',
    iconColor: 'text-purple-500 dark:text-purple-400',
    // Badge compatibility
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
  },
  
  // Completed/Closed/Done - Green (success)
  COMPLETED: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.checkCircle,
    // StatCard compatibility
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    // Badge compatibility
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  // Resolved - Teal (fixed but not closed)
  RESOLVED: {
    from: 'from-teal-500',
    to: 'to-teal-600',
    bg: 'bg-teal-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-teal-600',
    darkText: 'dark:text-teal-400',
    icon: ICONS.check,
    // StatCard compatibility
    gradient: 'bg-teal-50 dark:bg-slate-800',
    border: 'border-teal-200 dark:border-teal-500',
    textLabel: 'text-teal-700 dark:text-teal-300',
    textValue: 'text-teal-800 dark:text-teal-200',
    textSubtext: 'text-teal-600 dark:text-teal-400',
    iconBg: 'bg-teal-50 dark:bg-teal-900/40',
    iconColor: 'text-teal-500 dark:text-teal-400',
    // Badge compatibility
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  // Paused/On Hold - Indigo (temporarily stopped)
  PAUSED: {
    from: 'from-indigo-500',
    to: 'to-indigo-600',
    bg: 'bg-indigo-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-indigo-600',
    darkText: 'dark:text-indigo-400',
    icon: ICONS.pause,
    // StatCard compatibility
    gradient: 'bg-indigo-50 dark:bg-slate-800',
    border: 'border-indigo-200 dark:border-indigo-500',
    textLabel: 'text-indigo-700 dark:text-indigo-300',
    textValue: 'text-indigo-800 dark:text-indigo-200',
    textSubtext: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-50 dark:bg-indigo-900/40',
    iconColor: 'text-indigo-500 dark:text-indigo-400',
    // Badge compatibility
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  // Blocked - Fuchsia/Magenta (cannot proceed)
  BLOCKED: {
    from: 'from-fuchsia-500',
    to: 'to-fuchsia-600',
    bg: 'bg-fuchsia-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-fuchsia-600',
    darkText: 'dark:text-fuchsia-400',
    icon: ICONS.ban,
    // StatCard compatibility
    gradient: 'bg-fuchsia-50 dark:bg-slate-800',
    border: 'border-fuchsia-200 dark:border-fuchsia-500',
    textLabel: 'text-fuchsia-700 dark:text-fuchsia-300',
    textValue: 'text-fuchsia-800 dark:text-fuchsia-200',
    textSubtext: 'text-fuchsia-600 dark:text-fuchsia-400',
    iconBg: 'bg-fuchsia-50 dark:bg-fuchsia-900/40',
    iconColor: 'text-fuchsia-500 dark:text-fuchsia-400',
    // Badge compatibility
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  // Cancelled - Rose (stopped/abandoned)
  CANCELLED: {
    from: 'from-rose-500',
    to: 'to-rose-600',
    bg: 'bg-rose-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-rose-600',
    darkText: 'dark:text-rose-400',
    icon: ICONS.error,
    // StatCard compatibility
    gradient: 'bg-rose-50 dark:bg-slate-800',
    border: 'border-rose-200 dark:border-rose-500',
    textLabel: 'text-rose-700 dark:text-rose-300',
    textValue: 'text-rose-800 dark:text-rose-200',
    textSubtext: 'text-rose-600 dark:text-rose-400',
    iconBg: 'bg-rose-50 dark:bg-rose-900/40',
    iconColor: 'text-rose-500 dark:text-rose-400',
    // Badge compatibility
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  // Reopened - Amber (needs attention again)
  REOPENED: {
    from: 'from-amber-500',
    to: 'to-amber-600',
    bg: 'bg-amber-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-amber-600',
    darkText: 'dark:text-amber-400',
    icon: ICONS.refresh,
    // StatCard compatibility
    gradient: 'bg-amber-50 dark:bg-slate-800',
    border: 'border-amber-200 dark:border-amber-500',
    textLabel: 'text-amber-700 dark:text-amber-300',
    textValue: 'text-amber-800 dark:text-amber-200',
    textSubtext: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-50 dark:bg-amber-900/40',
    iconColor: 'text-amber-500 dark:text-amber-400',
    // Badge compatibility
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  },
  
  // Automated - Emerald (automated/good)
  AUTOMATED: {
    from: 'from-emerald-500',
    to: 'to-emerald-600',
    bg: 'bg-emerald-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-emerald-600',
    darkText: 'dark:text-emerald-400',
    icon: ICONS.cog,
    // StatCard compatibility
    gradient: 'bg-emerald-50 dark:bg-slate-800',
    border: 'border-emerald-200 dark:border-emerald-500',
    textLabel: 'text-emerald-700 dark:text-emerald-300',
    textValue: 'text-emerald-800 dark:text-emerald-200',
    textSubtext: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/40',
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    // Badge compatibility
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  // Semi-Automated - Cyan (partially automated)
  SEMI_AUTOMATED: {
    from: 'from-cyan-500',
    to: 'to-cyan-600',
    bg: 'bg-cyan-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-cyan-600',
    darkText: 'dark:text-cyan-400',
    icon: ICONS.sliders,
    // StatCard compatibility
    gradient: 'bg-cyan-50 dark:bg-slate-800',
    border: 'border-cyan-200 dark:border-cyan-500',
    textLabel: 'text-cyan-700 dark:text-cyan-300',
    textValue: 'text-cyan-800 dark:text-cyan-200',
    textSubtext: 'text-cyan-600 dark:text-cyan-400',
    iconBg: 'bg-cyan-50 dark:bg-cyan-900/40',
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    // Badge compatibility
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  // Manual - Violet (manual work)
  MANUAL: {
    from: 'from-violet-500',
    to: 'to-violet-600',
    bg: 'bg-violet-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-violet-600',
    darkText: 'dark:text-violet-400',
    icon: ICONS.user,
    // StatCard compatibility
    gradient: 'bg-violet-50 dark:bg-slate-800',
    border: 'border-violet-200 dark:border-violet-500',
    textLabel: 'text-violet-700 dark:text-violet-300',
    textValue: 'text-violet-800 dark:text-violet-200',
    textSubtext: 'text-violet-600 dark:text-violet-400',
    iconBg: 'bg-violet-50 dark:bg-violet-900/40',
    iconColor: 'text-violet-500 dark:text-violet-400',
    // Badge compatibility
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300'
  },
  
  // ============================================
  // Test Case & Test Run Specific Statuses
  // ============================================
  
  // Test Case Execution Statuses
  PASSED: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.checkCircle,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  FAILED: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.error,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  RETEST: {
    from: 'from-orange-500',
    to: 'to-orange-600',
    bg: 'bg-orange-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-orange-600',
    darkText: 'dark:text-orange-400',
    icon: ICONS.refresh,
    gradient: 'bg-orange-50 dark:bg-slate-800',
    border: 'border-orange-200 dark:border-orange-500',
    textLabel: 'text-orange-700 dark:text-orange-300',
    textValue: 'text-orange-800 dark:text-orange-200',
    textSubtext: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-50 dark:bg-orange-900/40',
    iconColor: 'text-orange-500 dark:text-orange-400',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  },
  
  SKIPPED: {
    from: 'from-purple-500',
    to: 'to-purple-600',
    bg: 'bg-purple-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-purple-600',
    darkText: 'dark:text-purple-400',
    icon: ICONS.refresh,
    gradient: 'bg-purple-50 dark:bg-slate-800',
    border: 'border-purple-200 dark:border-purple-500',
    textLabel: 'text-purple-700 dark:text-purple-300',
    textValue: 'text-purple-800 dark:text-purple-200',
    textSubtext: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/40',
    iconColor: 'text-purple-500 dark:text-purple-400',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
  },
  
  NOT_STARTED: {
    from: 'from-gray-500',
    to: 'to-gray-600',
    bg: 'bg-gray-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-gray-600',
    darkText: 'dark:text-gray-400',
    icon: ICONS.clock,
    gradient: 'bg-gray-50 dark:bg-slate-800',
    border: 'border-gray-200 dark:border-gray-500',
    textLabel: 'text-gray-700 dark:text-gray-300',
    textValue: 'text-gray-800 dark:text-gray-200',
    textSubtext: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gray-50 dark:bg-gray-900/40',
    iconColor: 'text-gray-500 dark:text-gray-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  },
  
  YET_TO_START: {
    from: 'from-sky-500',
    to: 'to-sky-600',
    bg: 'bg-sky-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-sky-600',
    darkText: 'dark:text-sky-400',
    icon: ICONS.clock,
    gradient: 'bg-sky-50 dark:bg-slate-800',
    border: 'border-sky-200 dark:border-sky-500',
    textLabel: 'text-sky-700 dark:text-sky-300',
    textValue: 'text-sky-800 dark:text-sky-200',
    textSubtext: 'text-sky-600 dark:text-sky-400',
    iconBg: 'bg-sky-50 dark:bg-sky-900/40',
    iconColor: 'text-sky-500 dark:text-sky-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  },
  
  // ============================================
  // Test Case Statuses
  // ============================================
  
  ACTIVE: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.checkCircle,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  DEPRECATED: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.warning,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  // ============================================
  // Priority Levels
  // ============================================
  
  CRITICAL: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.warning,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  HIGH: {
    from: 'from-orange-500',
    to: 'to-orange-600',
    bg: 'bg-orange-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-orange-600',
    darkText: 'dark:text-orange-400',
    icon: ICONS.arrowUp,
    gradient: 'bg-orange-50 dark:bg-slate-800',
    border: 'border-orange-200 dark:border-orange-500',
    textLabel: 'text-orange-700 dark:text-orange-300',
    textValue: 'text-orange-800 dark:text-orange-200',
    textSubtext: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-50 dark:bg-orange-900/40',
    iconColor: 'text-orange-500 dark:text-orange-400',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  },
  
  MEDIUM: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: ICONS.minus,
    gradient: 'bg-yellow-50 dark:bg-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  LOW: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.arrowDown,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  // ============================================
  // Test Case Types
  // ============================================
  
  FUNCTIONAL: {
    from: 'from-blue-500',
    to: 'to-blue-600',
    bg: 'bg-blue-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-blue-600',
    darkText: 'dark:text-blue-400',
    icon: ICONS.checkCircle,
    gradient: 'bg-blue-50 dark:bg-slate-800',
    border: 'border-blue-200 dark:border-blue-500',
    textLabel: 'text-blue-700 dark:text-blue-300',
    textValue: 'text-blue-800 dark:text-blue-200',
    textSubtext: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    iconColor: 'text-blue-500 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  NON_FUNCTIONAL: {
    from: 'from-purple-500',
    to: 'to-purple-600',
    bg: 'bg-purple-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-purple-600',
    darkText: 'dark:text-purple-400',
    icon: ICONS.shield,
    gradient: 'bg-purple-50 dark:bg-slate-800',
    border: 'border-purple-200 dark:border-purple-500',
    textLabel: 'text-purple-700 dark:text-purple-300',
    textValue: 'text-purple-800 dark:text-purple-200',
    textSubtext: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/40',
    iconColor: 'text-purple-500 dark:text-purple-400',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
  },
  
  API: {
    from: 'from-orange-500',
    to: 'to-orange-600',
    bg: 'bg-orange-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-orange-600',
    darkText: 'dark:text-orange-400',
    icon: ICONS.lightning,
    gradient: 'bg-orange-50 dark:bg-slate-800',
    border: 'border-orange-200 dark:border-orange-500',
    textLabel: 'text-orange-700 dark:text-orange-300',
    textValue: 'text-orange-800 dark:text-orange-200',
    textSubtext: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-50 dark:bg-orange-900/40',
    iconColor: 'text-orange-500 dark:text-orange-400',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  },
  
  UI: {
    from: 'from-pink-500',
    to: 'to-pink-600',
    bg: 'bg-pink-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-pink-600',
    darkText: 'dark:text-pink-400',
    icon: ICONS.eye,
    gradient: 'bg-pink-50 dark:bg-slate-800',
    border: 'border-pink-200 dark:border-pink-500',
    textLabel: 'text-pink-700 dark:text-pink-300',
    textValue: 'text-pink-800 dark:text-pink-200',
    textSubtext: 'text-pink-600 dark:text-pink-400',
    iconBg: 'bg-pink-50 dark:bg-pink-900/40',
    iconColor: 'text-pink-500 dark:text-pink-400',
    badge: 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300'
  },
  
  PERFORMANCE: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: ICONS.lightning,
    gradient: 'bg-yellow-50 dark:bg-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  SECURITY: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.shield,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  // ============================================
  // Case Types (Sanity, Smoke, Regression)
  // ============================================
  
  SANITY: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.checkCircle,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  SMOKE: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: ICONS.warning,
    gradient: 'bg-yellow-50 dark:bg-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  
  REGRESSION: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.refresh,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  // ============================================
  // Bug/Issue Types
  // ============================================
  
  BUG: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-400',
    icon: ICONS.bug,
    gradient: 'bg-red-50 dark:bg-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  
  FEATURE: {
    from: 'from-blue-500',
    to: 'to-blue-600',
    bg: 'bg-blue-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-blue-600',
    darkText: 'dark:text-blue-400',
    icon: ICONS.feature,
    gradient: 'bg-blue-50 dark:bg-slate-800',
    border: 'border-blue-200 dark:border-blue-500',
    textLabel: 'text-blue-700 dark:text-blue-300',
    textValue: 'text-blue-800 dark:text-blue-200',
    textSubtext: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    iconColor: 'text-blue-500 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  ENHANCEMENT: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.plus,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  TASK: {
    from: 'from-gray-500',
    to: 'to-gray-600',
    bg: 'bg-gray-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-gray-600',
    darkText: 'dark:text-gray-400',
    icon: ICONS.check,
    gradient: 'bg-gray-50 dark:bg-slate-800',
    border: 'border-gray-200 dark:border-gray-500',
    textLabel: 'text-gray-700 dark:text-gray-300',
    textValue: 'text-gray-800 dark:text-gray-200',
    textSubtext: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gray-50 dark:bg-gray-900/40',
    iconColor: 'text-gray-500 dark:text-gray-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  },
  
  // ============================================
  // Project Statuses
  // ============================================
  
  ARCHIVED: {
    from: 'from-gray-500',
    to: 'to-gray-600',
    bg: 'bg-gray-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-gray-600',
    darkText: 'dark:text-gray-400',
    icon: ICONS.archive,
    gradient: 'bg-gray-50 dark:bg-slate-800',
    border: 'border-gray-200 dark:border-gray-500',
    textLabel: 'text-gray-700 dark:text-gray-300',
    textValue: 'text-gray-800 dark:text-gray-200',
    textSubtext: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gray-50 dark:bg-gray-900/40',
    iconColor: 'text-gray-500 dark:text-gray-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  },
  
  // ============================================
  // Team Visibility
  // ============================================
  
  PUBLIC: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.checkCircle,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  PRIVATE: {
    from: 'from-gray-500',
    to: 'to-gray-600',
    bg: 'bg-gray-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-gray-600',
    darkText: 'dark:text-gray-400',
    icon: ICONS.lock,
    gradient: 'bg-gray-50 dark:bg-slate-800',
    border: 'border-gray-200 dark:border-gray-500',
    textLabel: 'text-gray-700 dark:text-gray-300',
    textValue: 'text-gray-800 dark:text-gray-200',
    textSubtext: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gray-50 dark:bg-gray-900/40',
    iconColor: 'text-gray-500 dark:text-gray-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  },
  
  // ============================================
  // Activity Types (for activity log)
  // ============================================
  
  TEAM: {
    from: 'from-blue-500',
    to: 'to-blue-600',
    bg: 'bg-blue-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-blue-600',
    darkText: 'dark:text-blue-400',
    icon: ICONS.team,
    gradient: 'bg-blue-50 dark:bg-slate-800',
    border: 'border-blue-200 dark:border-blue-500',
    textLabel: 'text-blue-700 dark:text-blue-300',
    textValue: 'text-blue-800 dark:text-blue-200',
    textSubtext: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    iconColor: 'text-blue-500 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  
  PROJECT: {
    from: 'from-green-500',
    to: 'to-green-600',
    bg: 'bg-green-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-green-600',
    darkText: 'dark:text-green-400',
    icon: ICONS.project,
    gradient: 'bg-green-50 dark:bg-slate-800',
    border: 'border-green-200 dark:border-green-500',
    textLabel: 'text-green-700 dark:text-green-300',
    textValue: 'text-green-800 dark:text-green-200',
    textSubtext: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/40',
    iconColor: 'text-green-500 dark:text-green-400',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  },
  
  TESTCASE: {
    from: 'from-purple-500',
    to: 'to-purple-600',
    bg: 'bg-purple-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-purple-600',
    darkText: 'dark:text-purple-400',
    icon: ICONS.testCase,
    gradient: 'bg-purple-50 dark:bg-slate-800',
    border: 'border-purple-200 dark:border-purple-500',
    textLabel: 'text-purple-700 dark:text-purple-300',
    textValue: 'text-purple-800 dark:text-purple-200',
    textSubtext: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/40',
    iconColor: 'text-purple-500 dark:text-purple-400',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
  },
  
  TESTRUN: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: ICONS.testRun,
    gradient: 'bg-yellow-50 dark:bg-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  }
}

/**
 * Helper function to get color by status name
 * Normalizes status names to match constant keys
 */
export const getStatusColor = (status) => {
  if (!status) return STATUS_COLORS.DRAFT
  
  const normalizedStatus = status.toString().toUpperCase().replace(/[- ]/g, '_')
  
  // Map common status variations
  const statusMap = {
    // Main flow statuses
    'OPEN': STATUS_COLORS.OPEN,
    'NEW': STATUS_COLORS.OPEN,
    'DRAFT': STATUS_COLORS.DRAFT,
    'YET_TO_START': STATUS_COLORS.YET_TO_START,
    'NOT_STARTED': STATUS_COLORS.NOT_STARTED,
    'IN_PROGRESS': STATUS_COLORS.IN_PROGRESS,
    'INPROGRESS': STATUS_COLORS.IN_PROGRESS,
    'IN_REVIEW': STATUS_COLORS.IN_REVIEW,
    'REVIEW': STATUS_COLORS.IN_REVIEW,
    'TESTING': STATUS_COLORS.TESTING,
    'COMPLETED': STATUS_COLORS.COMPLETED,
    'COMPLETE': STATUS_COLORS.COMPLETED,
    'CLOSED': STATUS_COLORS.COMPLETED,
    'DONE': STATUS_COLORS.COMPLETED,
    'RESOLVED': STATUS_COLORS.RESOLVED,
    'PAUSED': STATUS_COLORS.PAUSED,
    'ON_HOLD': STATUS_COLORS.PAUSED,
    'BLOCKED': STATUS_COLORS.BLOCKED,
    'CANCELLED': STATUS_COLORS.CANCELLED,
    'CANCELED': STATUS_COLORS.CANCELLED,
    'REOPENED': STATUS_COLORS.REOPENED,
    
    // Test case execution statuses
    'PASSED': STATUS_COLORS.PASSED,
    'FAILED': STATUS_COLORS.FAILED,
    'RETEST': STATUS_COLORS.RETEST,
    'SKIPPED': STATUS_COLORS.SKIPPED,
    
    // Test case statuses
    'ACTIVE': STATUS_COLORS.ACTIVE,
    'DEPRECATED': STATUS_COLORS.DEPRECATED,
    
    // Priority levels
    'CRITICAL': STATUS_COLORS.CRITICAL,
    'HIGH': STATUS_COLORS.HIGH,
    'MEDIUM': STATUS_COLORS.MEDIUM,
    'LOW': STATUS_COLORS.LOW,
    
    // Test case types
    'FUNCTIONAL': STATUS_COLORS.FUNCTIONAL,
    'NON_FUNCTIONAL': STATUS_COLORS.NON_FUNCTIONAL,
    'API': STATUS_COLORS.API,
    'UI': STATUS_COLORS.UI,
    'PERFORMANCE': STATUS_COLORS.PERFORMANCE,
    'SECURITY': STATUS_COLORS.SECURITY,
    
    // Case types
    'SANITY': STATUS_COLORS.SANITY,
    'SMOKE': STATUS_COLORS.SMOKE,
    'REGRESSION': STATUS_COLORS.REGRESSION,
    
    // Automation status
    'AUTOMATED': STATUS_COLORS.AUTOMATED,
    'SEMI_AUTOMATED': STATUS_COLORS.SEMI_AUTOMATED,
    'MANUAL': STATUS_COLORS.MANUAL,
    
    // Bug/Issue types
    'BUG': STATUS_COLORS.BUG,
    'FEATURE': STATUS_COLORS.FEATURE,
    'ENHANCEMENT': STATUS_COLORS.ENHANCEMENT,
    'TASK': STATUS_COLORS.TASK,
    
    // Project statuses
    'ARCHIVED': STATUS_COLORS.ARCHIVED,
    
    // Team visibility
    'PUBLIC': STATUS_COLORS.PUBLIC,
    'PRIVATE': STATUS_COLORS.PRIVATE,
    
    // Activity types
    'TEAM': STATUS_COLORS.TEAM,
    'PROJECT': STATUS_COLORS.PROJECT,
    'TESTCASE': STATUS_COLORS.TESTCASE,
    'TESTRUN': STATUS_COLORS.TESTRUN,
    'TOTAL': STATUS_COLORS.TOTAL
  }
  
  return statusMap[normalizedStatus] || STATUS_COLORS.DRAFT
}

